import type { FtueJourneyId, FtuePersistedState } from './types';

/** Bump when the guided tour steps or major help structure change. */
export const FTUE_VERSION = 3;

const STORAGE_KEY = 'aleno_ops_ftue_v1';

const JOURNEY_IDS = new Set<FtueJourneyId>([
  'full',
  'surface.live',
  'surface.performance',
  'surface.settings',
  'surface.comparison.live',
  'surface.comparison.performance',
  'surface.comparison.settings',
  'deep.live.zones',
  'deep.perf.zones',
  'deep.perf.drill',
  'deep.settings.zones',
]);

const DEFAULT_STATE: FtuePersistedState = {
  completedVersion: null,
  skippedVersion: null,
  completedJourneyVersions: {},
  lastStepId: null,
  lastJourneyId: null,
  welcomeShownVersion: null,
  dismissedWhatsNewVersions: [],
};

function isObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function coerceJourneyId(raw: unknown): FtueJourneyId | null {
  return typeof raw === 'string' && JOURNEY_IDS.has(raw as FtueJourneyId)
    ? (raw as FtueJourneyId)
    : null;
}

export function coerceFtuePersistedState(raw: unknown): FtuePersistedState {
  if (!isObject(raw)) return { ...DEFAULT_STATE };
  const dismissed = Array.isArray(raw.dismissedWhatsNewVersions)
    ? raw.dismissedWhatsNewVersions.filter(
        (n): n is number => typeof n === 'number' && Number.isFinite(n),
      )
    : [];
  const completedJourneyVersions: Partial<Record<FtueJourneyId, number>> = {};
  if (isObject(raw.completedJourneyVersions)) {
    for (const [key, value] of Object.entries(raw.completedJourneyVersions)) {
      const journeyId = coerceJourneyId(key);
      if (
        journeyId &&
        typeof value === 'number' &&
        Number.isFinite(value)
      ) {
        completedJourneyVersions[journeyId] = value;
      }
    }
  }
  return {
    completedVersion:
      typeof raw.completedVersion === 'number' ? raw.completedVersion : null,
    skippedVersion:
      typeof raw.skippedVersion === 'number' ? raw.skippedVersion : null,
    completedJourneyVersions,
    lastStepId: typeof raw.lastStepId === 'string' ? raw.lastStepId : null,
    lastJourneyId: coerceJourneyId(raw.lastJourneyId),
    welcomeShownVersion:
      typeof raw.welcomeShownVersion === 'number'
        ? raw.welcomeShownVersion
        : null,
    dismissedWhatsNewVersions: dismissed,
  };
}

export function loadFtuePersistedState(): FtuePersistedState {
  if (typeof window === 'undefined') return { ...DEFAULT_STATE };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw == null) return { ...DEFAULT_STATE };
    return coerceFtuePersistedState(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_STATE };
  }
}

export function saveFtuePersistedState(state: FtuePersistedState): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
}

export function patchFtuePersistedState(
  patch: Partial<FtuePersistedState>,
): FtuePersistedState {
  const next = { ...loadFtuePersistedState(), ...patch };
  saveFtuePersistedState(next);
  return next;
}

/** Auto-open welcome when this browser has never completed/skipped this version. */
export function shouldAutoOpenWelcome(state: FtuePersistedState): boolean {
  if (state.completedVersion === FTUE_VERSION) return false;
  if (state.skippedVersion === FTUE_VERSION) return false;
  if (state.welcomeShownVersion === FTUE_VERSION) return false;
  return true;
}

/** Non-blocking “What’s new” when a prior version was finished and content bumped. */
export function shouldShowWhatsNew(state: FtuePersistedState): boolean {
  if (state.completedVersion == null && state.skippedVersion == null) {
    return false;
  }
  const lastSeen = Math.max(
    state.completedVersion ?? 0,
    state.skippedVersion ?? 0,
  );
  if (lastSeen >= FTUE_VERSION) return false;
  if (state.dismissedWhatsNewVersions.includes(FTUE_VERSION)) return false;
  return true;
}

export function markWelcomeShown(): FtuePersistedState {
  return patchFtuePersistedState({ welcomeShownVersion: FTUE_VERSION });
}

export function markTourSkipped(
  lastStepId: string | null,
  lastJourneyId: FtueJourneyId | null = 'full',
): FtuePersistedState {
  if (lastJourneyId !== 'full') {
    return loadFtuePersistedState();
  }
  return patchFtuePersistedState({
    skippedVersion: FTUE_VERSION,
    welcomeShownVersion: FTUE_VERSION,
    lastStepId,
    lastJourneyId,
  });
}

export function markTourCompleted(
  journeyId: FtueJourneyId = 'full',
): FtuePersistedState {
  const current = loadFtuePersistedState();
  const completedJourneyVersions = {
    ...current.completedJourneyVersions,
    [journeyId]: FTUE_VERSION,
  };
  if (journeyId !== 'full') {
    return patchFtuePersistedState({ completedJourneyVersions });
  }
  return patchFtuePersistedState({
    completedVersion: FTUE_VERSION,
    completedJourneyVersions,
    skippedVersion: null,
    welcomeShownVersion: FTUE_VERSION,
    lastStepId: null,
    lastJourneyId: null,
  });
}

export function markTourProgress(
  stepId: string,
  journeyId: FtueJourneyId = 'full',
): FtuePersistedState {
  // Only the full product tour participates in Resume.
  if (journeyId !== 'full') {
    return loadFtuePersistedState();
  }
  return patchFtuePersistedState({
    lastStepId: stepId,
    lastJourneyId: journeyId,
  });
}

export function dismissWhatsNew(): FtuePersistedState {
  const cur = loadFtuePersistedState();
  if (cur.dismissedWhatsNewVersions.includes(FTUE_VERSION)) return cur;
  return patchFtuePersistedState({
    dismissedWhatsNewVersions: [
      ...cur.dismissedWhatsNewVersions,
      FTUE_VERSION,
    ],
  });
}

export function clearFtueProgressForTests(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
